import React, { useEffect, useRef, useState } from 'react';
import { Container, TextField, Grid, FormControl, RadioGroup, FormControlLabel, Radio, CircularProgress } from '@material-ui/core';
import BlueButton from '../../components/button/BlueButton';
import RefreshIcon from '@material-ui/icons/Refresh';
import contraSenhaService from '../../Services/ContraSenha/contra-senha-service';
import CustomAlert from '../../components/alert/CustomAlert';
import { ACAO_CONTRA_SENHA, consumirRetornoFacialOS, iniciarEtapaFacialOS } from '../../Services/biometria/etapa-facial-os';


// A NA vem da OS no fluxo normal, ou direto pela prop quando a tela de
// contra senha e usada para uma NA que ja saiu da agenda.
export default function ContraSenhaComponent({ os, na }) {
  const naSelecionada = na ?? os?.na;
  const [opcao, setOpcao] = useState('');
  const [ocorrencia, setOcorrencia] = useState('');
  const [contraSenha, setContraSenha] = React.useState('');
  const [efeitoCarregando, setEfeitoCarregando] = React.useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState({ title: '', body: '' });
  const [alertType, setAlertType] = useState('success');

  // Evita duplo envio: uma geração por vez (o comprovante facial também é de uso único na API).
  const gerandoContraSenha = useRef(false);
  // A volta da validação facial é lida uma única vez por componente.
  const retornoFacialLido = useRef(false);

  const exibirAlerta = (title, body, type) => {
    setAlertMessage({ title, body });
    setAlertType(type);
    setAlertOpen(true);
  };

  /**
   * Gera a contra senha. Com exigência facial a API responde 428: sem comprovante leva o técnico à validação
   * (opção, ocorrência e NA ficam guardadas na aba e a geração é refeita na volta); com comprovante recusado, só avisa.
   */
  const gerarContraSenha = async (opcaoGerar, ocorrenciaGerar, naGerar, codigoFacial = null) => {
    if (gerandoContraSenha.current) return;
    gerandoContraSenha.current = true;
    setEfeitoCarregando(true);

    let contraSenhaResult = await contraSenhaService.contraSenha(opcaoGerar, ocorrenciaGerar, naGerar, codigoFacial);

    if (contraSenhaResult?.etapaFacial) {
      // Comprovante recusado na volta (expirado, outra NA/ação): não entra em ciclo, o técnico tenta de novo.
      let mensagem = contraSenhaResult.mensagem || 'Validação facial inválida ou expirada. Gere a contra senha novamente.';
      if (!codigoFacial) {
        const redirecionou = await iniciarEtapaFacialOS({
          acao: ACAO_CONTRA_SENHA,
          atividadeId: contraSenhaResult.atividadeId,
          na: contraSenhaResult.na || naGerar,
          returnPath: window.location.pathname,
          extra: { opcao: opcaoGerar, ocorrencia: ocorrenciaGerar, na: naGerar },
        });
        // O navegador já está indo para o front de biometria; o botão segue travado.
        if (redirecionou) return;
        mensagem = 'Não foi possível abrir a validação facial. Tente novamente em instantes.';
      }
      setContraSenha('');
      exibirAlerta('Atenção!', mensagem, 'fail');
    } else if (contraSenhaResult && !contraSenhaResult.error) {
      setContraSenha(contraSenhaResult.contraSenha);
      exibirAlerta('Sucesso!', 'Contrassenha gerada com sucesso!', 'success');
    } else {
      setContraSenha('');
      exibirAlerta(
        'Atenção!',
        contraSenhaResult?.message ||
          'Ocorrência não encontrada! Por favor válidar número da ocorrência.',
        'fail'
      );
    }

    gerandoContraSenha.current = false;
    setEfeitoCarregando(false);
  };

  // Volta da validação facial (?facial=): refaz a mesma geração com o comprovante (uso único, desta NA e da ação
  // CONTRA_SENHA). Espera a NA estar disponível: na tela da OS ela chega depois de carregar a OS.
  useEffect(() => {
    if (retornoFacialLido.current || !naSelecionada) return;
    retornoFacialLido.current = true;

    const retorno = consumirRetornoFacialOS(ACAO_CONTRA_SENHA);
    if (!retorno) return;
    if (retorno.expirado) {
      exibirAlerta('Atenção!', 'A validação facial expirou. Gere a contra senha novamente.', 'fail');
      return;
    }

    const pendente = retorno.pendente.extra || {};
    if (pendente.na !== naSelecionada || !pendente.ocorrencia) {
      // O comprovante já foi consumido e só vale para a NA dele: avisa em vez de descartar em silêncio.
      exibirAlerta('Atenção!', 'A validação facial era de outra NA. Gere a contra senha novamente.', 'fail');
      return;
    }

    setOpcao(pendente.opcao ? String(pendente.opcao) : '');
    setOcorrencia(pendente.ocorrencia);
    gerarContraSenha(pendente.opcao, pendente.ocorrencia, pendente.na, retorno.codigo);
    // Só naSelecionada: o retorno é lido uma vez (guard retornoFacialLido, também contra o efeito duplo do
    // StrictMode) assim que a NA existe; gerarContraSenha/exibirAlerta ficam fora das dependências de propósito.
  }, [naSelecionada]);

  const handleContraSenha = async (event) => {
    event.preventDefault();

    if(ocorrencia === ''){
      setAlertMessage({ title: 'Atenção!', body: 'É obrigatório o preenchimento da ocorrência.' });
      setAlertType('fail');
      setAlertOpen(true);
      return;
    }

    if(!naSelecionada){
      setAlertMessage({ title: 'Atenção!', body: 'Informe o número da NA para gerar a contra senha.' });
      setAlertType('fail');
      setAlertOpen(true);
      return;
    }

    await gerarContraSenha(opcao, ocorrencia, naSelecionada);
  };

  return (
    <Container>
      <div>
        <form onSubmit={handleContraSenha} required>
          <Grid container style={{ flexGrow: 1 }} spacing={3}>
            <Grid xs={12} md={12} lg={5} item>
              <FormControl component="fieldset">
                <RadioGroup
                  row
                  aria-label="rdOpcao"
                  name="rdOpcao"
                  value={opcao}
                  onChange={(e) => setOpcao(e.target.value)}
                >
                  <FormControlLabel
                    value="1"
                    control={<Radio required color="primary" />}
                    label="Modelo RI/Configuração"
                  />
                  <FormControlLabel
                    value="2"
                    control={<Radio required color="primary" />}
                    label="Opções Avançadas"
                  />
                </RadioGroup>
              </FormControl>
            </Grid>
            <Grid xs={12} md={12} lg={3} item>
              <TextField
                variant="outlined"
                name="txtOcorrencia"
                value={ocorrencia}
                onChange={(e) => setOcorrencia(e.target.value)}
                label={
                  <span>
                    Ocorrência <span style={{ color: 'red' }}>*</span>
                  </span>
                }
                placeholder="Ocorrência"
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid xs={12} md={12} lg={3} item>
              <div>
                <BlueButton
                  fullWidth
                  variant="contained"
                  color="primary"
                  type="submit"
                  style={{ marginBottom: '20px', height: 55 }}
                  startIcon={efeitoCarregando ? <CircularProgress size={20} /> : <RefreshIcon />}
                  disabled={efeitoCarregando}
                >
                  <span style={{ fontSize: '0.7rem' }}>
                    {efeitoCarregando ? 'Gerando... ' : 'Gerar'}
                  </span>
                </BlueButton>
              </div>
            </Grid>
          </Grid>
        </form>

        <TextField
          multiline
          name="txtContraSenha"
          value={contraSenha}
          fullWidth
          disabled
          InputLabelProps={{
            shrink: true,
          }}
        />

        <CustomAlert
          open={alertOpen}
          message={alertMessage}
          type={alertType}
          onClose={() => setAlertOpen(false)}
        />
      </div>
    </Container>
  );
}
